import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import type { LoginCredentials } from '../types/auth.types';
import { isTwoFactorChallenge } from '../types/auth.types';
import { getTenantSlug, setTenantSlug, isTenantLocked } from '../utils/tenant';
import { fetchPublicBranding, type PublicBranding } from '../services/auth.service';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

interface FieldErrors {
  email?: string;
  password?: string;
}

interface ApiAuthError {
  code?: string;
  message?: string;
}

function getBannerMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return 'Your email or password is incorrect. Please try again.';
    case 'USER_NOT_FOUND':
      return 'No account found with this email address. Contact HR.';
    case 'TOO_MANY_ATTEMPTS':
      return 'Too many login attempts. Please wait a minute and try again.';
    case 'ACCOUNT_LOCKED':
      return fallback || 'Your account is temporarily locked. Please try again later.';
    case 'AMBIGUOUS_COMPANY':
      return 'This email is used in more than one company. Enter your Organisation ID above and sign in again. If you do not know it, ask your HR administrator.';
    case 'NO_COMPANY':
      return 'Your account is not assigned to a company yet, so you cannot sign in. Contact your HR administrator.';
    case '2FA_REQUIRED_GRACE_EXPIRED':
      return 'Your account requires two-factor authentication. Contact IT to reset.';
    default:
      return fallback || 'Something went wrong. Please try again or contact IT.';
  }
}

function validateForm(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  if (!email) {
    errors.email = 'Please enter a valid email address';
  } else if (!/^[^\s@]+@[^\s@]+(\.[^\s@]+)+$/.test(email)) {
    errors.email = 'Please enter a valid email address';
  }
  if (!password) {
    errors.password = 'Password must be at least 8 characters';
  } else if (password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }
  return errors;
}

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, user } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [bannerError, setBannerError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Tenant slug — from subdomain (locked) or user-entered (editable)
  const tenantLocked = isTenantLocked();
  const [tenantSlug, setTenantSlugState] = useState<string>(() => {
    // A link such as /login?tenant=acme pre-fills the company ID (and so its name and logo).
    const fromLink = new URLSearchParams(window.location.search).get('tenant')?.toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (fromLink && !isTenantLocked()) {
      setTenantSlug(fromLink);
      return fromLink;
    }
    return getTenantSlug() ?? '';
  });
  const [branding, setBranding] = useState<PublicBranding | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // The company's own name and logo, once its ID is known. Neutral until then.
  useEffect(() => {
    if (!tenantSlug) { setBranding(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      void fetchPublicBranding(tenantSlug).then((b) => { if (!cancelled) setBranding(b); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [tenantSlug]);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      navigate(
        user.role === 'super_admin' ? '/admin/tenants' : '/dashboard',
        { replace: true },
      );
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBannerError('');
    const errors = validateForm(email, password);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      const credentials: LoginCredentials = { email: email.trim(), password };
      const outcome = await login(credentials);

      // Branch 1: 2FA required — stash challenge token, route to 2FA page (UI #4)
      if (isTwoFactorChallenge(outcome)) {
        sessionStorage.setItem('attendza_2fa_challenge', outcome.twoFactorToken);
        navigate('/login/2fa', { replace: true });
        return;
      }

      // Branch 2: login succeeded — route to password change first if expired
      if (outcome.passwordExpired) {
        navigate('/profile/security/password?expired=1', { replace: true });
        return;
      }

      // Branch 3: normal login
      navigate(
        outcome.user.role === 'super_admin' ? '/admin/tenants' : '/dashboard',
        { replace: true },
      );
    } catch (err: unknown) {
      const apiError = err as ApiAuthError;
      setBannerError(getBannerMessage(apiError.code, apiError.message ?? ''));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      className={[
        'flex min-h-dvh flex-col items-center justify-center bg-background px-4 py-6',
        'transition-opacity duration-200',
        mounted ? 'opacity-100' : 'opacity-0',
      ].join(' ')}
    >
      {/* Logo */}
      <div className="mb-8 text-center">
        {branding?.logoUrl && (
          <img
            src={branding.logoUrl}
            alt=""
            className="mx-auto mb-3 block max-h-16 max-w-[220px] object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <h1 className="m-0 font-heading text-4xl font-bold tracking-tight text-foreground">
          {branding?.name ?? 'Attendza'}
        </h1>
        <p className="mt-1.5 font-body text-15 text-muted-foreground">
          Welcome back. Please sign in.
        </p>
      </div>

      {/* Card */}
      <Card className="w-full max-w-[420px] gap-0 p-8 shadow-sm">
        {/* Error banner */}
        {bannerError && (
          <Alert variant="destructive" className="mb-5">
            <AlertCircle />
            <AlertDescription>{bannerError}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <FieldGroup>
            {/* Tenant slug — hidden when subdomain is already set */}
            {!tenantLocked && (
              <Field>
                <FieldLabel htmlFor="tenant">Organisation ID</FieldLabel>
                <Input
                  id="tenant"
                  type="text"
                  autoComplete="organization"
                  placeholder="e.g. acme"
                  value={tenantSlug}
                  onChange={(e) => {
                    const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    setTenantSlugState(v);
                    setTenantSlug(v || null);
                  }}
                />
                <FieldDescription>
                  The short name for your organisation. Leave blank to log in as platform admin.
                </FieldDescription>
              </Field>
            )}

            {/* Email field. htmlFor="email" is an e2e contract — _login.ts
                finds this input via getByLabel(/email/i). */}
            <Field data-invalid={!!fieldErrors.email || undefined}>
              <FieldLabel htmlFor="email">Email address</FieldLabel>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: undefined })); }}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && <FieldError id="email-error">{fieldErrors.email}</FieldError>}
            </Field>

            {/* Password field — htmlFor="password" is likewise an e2e contract. */}
            <Field data-invalid={!!fieldErrors.password || undefined}>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFieldErrors(p => ({ ...p, password: undefined })); }}
                  className="pr-11"
                  aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                  aria-invalid={!!fieldErrors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center border-none bg-transparent text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldErrors.password && (
                <FieldError id="password-error">{fieldErrors.password}</FieldError>
              )}
            </Field>

            {/* Submit. The accessible name "Sign In" is an e2e contract —
                _login.ts clicks getByRole('button', { name: /sign in|log in/i }). */}
            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </FieldGroup>
        </form>
      </Card>

      {/* Footer help text */}
      <p className="mt-6 text-center font-body text-13 text-muted-foreground">
        Having trouble signing in? Contact your HR administrator
      </p>
      {/* The injected style element is gone: `spin` is declared globally in
          index.css, `slideDown` died with the hand-rolled error banner, and the
          prefers-reduced-motion rule moved to index.css — a page component
          should not inject a global `*` rule that outlives its own layout
          concerns. */}
    </div>
  );
}
